/**
 * Statü rozeti.
 *
 * Renk artık koda gömülü değil: `statuses/{technicName}.color` alanından gelir
 * (eski sistemde rozet renkleri her ekranda ayrı ayrı sabitlenmişti — üretim
 * listesinde yeşil olan rozet sipariş listesinde griydi).
 * Renk gelmezse teknik ada göre kararlı bir varsayılana düşer.
 */

export type BadgeTone =
  | "gray"
  | "blue"
  | "green"
  | "amber"
  | "red"
  | "purple"
  | "slate";

const TONE_CLASS: Record<BadgeTone, string> = {
  gray: "bg-gray-50 text-gray-700 ring-gray-200",
  slate: "bg-slate-50 text-slate-700 ring-slate-200",
  blue: "bg-sky-50 text-sky-800 ring-sky-200",
  green: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  amber: "bg-amber-50 text-amber-900 ring-amber-200",
  red: "bg-rose-50 text-rose-800 ring-rose-200",
  purple: "bg-violet-50 text-violet-800 ring-violet-200",
};

const KNOWN_TONES: BadgeTone[] = ["gray", "slate", "blue", "green", "amber", "red", "purple"];

/**
 * ★ ANLAMSAL TAKMA ADLAR.
 *
 * Beş çağrı yeri renk yerine ANLAM gönderiyordu (`success`, `danger`, `neutral`,
 * `warning`). Bunlar `KNOWN_TONES` testini geçemediği için sessizce yok sayılıp
 * `fallbackTone("")` → GRİ oluyordu. Sonuç: Denetim Kaydı'nda "Başarılı" ile
 * "Hata", Ürünler'de "Yayında" ile "Arşiv" birebir aynı gri rozetti — renkle
 * taşınan bütün anlam kayıptı.
 *
 * Çağrı yerlerini tek tek düzeltmek yerine takma adları burada tanımak, aynı
 * hatanın altıncı kez yazılmasını da engeller.
 */
const TONE_ALIAS: Record<string, BadgeTone> = {
  success: "green",
  ok: "green",
  danger: "red",
  error: "red",
  warning: "amber",
  warn: "amber",
  neutral: "gray",
  info: "blue",
};

/** Teknik statü adından anlamlı bir varsayılan renk türetir. */
function fallbackTone(technicName: string): BadgeTone {
  const t = technicName.toLowerCase();
  if (t.includes("completed") || t.includes("tamamlan") || t.includes("teslim")) return "green";
  if (t.includes("cancel") || t.includes("iptal") || t.includes("fail") || t.includes("reject"))
    return "red";
  if (t.includes("hold") || t.includes("bekle") || t.includes("pending")) return "amber";
  if (t.includes("uretim") || t.includes("prod") || t.includes("processing")) return "blue";
  if (t.includes("refund") || t.includes("iade")) return "purple";
  return "gray";
}

interface StatusBadgeProps {
  /** Ekranda görünen Türkçe etiket (statuses.label). */
  label?: string | null;
  /** Teknik ad — etiket yoksa gösterilir, renk türetiminde kullanılır. */
  technicName?: string | null;
  /** statuses.color alanı ("green", "amber"...). Bilinmeyen değer yok sayılır. */
  color?: string | null;
  size?: "sm" | "md";
  className?: string;
}

export function StatusBadge({
  label,
  technicName,
  color,
  size = "sm",
  className = "",
}: StatusBadgeProps) {
  const tech = technicName ?? "";
  const key = (color ?? "").trim().toLowerCase();
  const tone: BadgeTone = (KNOWN_TONES as string[]).includes(key)
    ? (key as BadgeTone)
    : (TONE_ALIAS[key] ?? fallbackTone(tech));

  const text = (label ?? "").trim() || tech || "—";
  const pad = size === "md" ? "px-2.5 py-1 text-sm" : "px-2 py-0.5 text-xs";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ring-1 ring-inset ${TONE_CLASS[tone]} ${pad} ${className}`}
      title={tech && tech !== text ? tech : undefined}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-70" />
      {text}
    </span>
  );
}

export default StatusBadge;
